'use client';

import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useSPAPathname, useSPARouter, setParams, notifyParams } from '../context/SPARouterContext';
import { DEFAULT_PAGE_META, formatDocumentTitle, getPageMeta } from '@/utils/routeMeta';

// Lazy-loaded views (code-split per route)
const HomePage = lazy(() => import('./HomePage'));
const FavoritesPage = lazy(() => import('./FavoritesPage'));
const SellPage = lazy(() => import('./SellPage'));
const SearchPage = lazy(() => import('./SearchPage'));
const ProductDetailPage = lazy(() => import('./ProductDetailPage'));
const ArticlePage = lazy(() => import('./ArticlePage'));
const BlogPage = lazy(() => import('./BlogPage'));
const AuctionDetailPage = lazy(() => import('./AuctionDetailPage'));
const AuctionPage = lazy(() => import('./AuctionPage'));
const BundleDetailPage = lazy(() => import('./BundleDetailPage'));
const BundlesPage = lazy(() => import('./BundlesPage'));
const CategoriesPage = lazy(() => import('./CategoriesPage'));
const CharityPage = lazy(() => import('./CharityPage'));
const ChatPage = lazy(() => import('./ChatPage'));
const CheckoutPage = lazy(() => import('./CheckoutPage'));
const ComparePage = lazy(() => import('./ComparePage'));
const ContactPage = lazy(() => import('./ContactPage'));
const CouponsPage = lazy(() => import('./CouponsPage'));
const DashboardPage = lazy(() => import('./DashboardPage'));
const EventDetailPage = lazy(() => import('./EventDetailPage'));
const EventsPage = lazy(() => import('./EventsPage'));
const FAQPage = lazy(() => import('./FAQPage'));
const FeedPage = lazy(() => import('./FeedPage'));
const ForumPage = lazy(() => import('./ForumPage'));
const ForumPostPage = lazy(() => import('./ForumPostPage'));
const HelpCenterPage = lazy(() => import('./HelpCenterPage'));
const InsurancePage = lazy(() => import('./InsurancePage'));
const LostFoundPage = lazy(() => import('./LostFoundPage'));
const ManageProductsPage = lazy(() => import('./ManageProductsPage'));
const MapPage = lazy(() => import('./MapPage'));
const NotFoundPage = lazy(() => import('./NotFoundPage'));
const NotificationsPage = lazy(() => import('./NotificationsPage'));
const OrdersPage = lazy(() => import('./OrdersPage'));
const PetHealthPage = lazy(() => import('./PetHealthPage'));
const ProfilePage = lazy(() => import('./ProfilePage'));
const ReferralPage = lazy(() => import('./ReferralPage'));
const ReturnsPage = lazy(() => import('./ReturnsPage'));
// SellPageView removed — SellPage already imported above
const ServiceDetailPage = lazy(() => import('./ServiceDetailPage'));
const ServicesPage = lazy(() => import('./ServicesPage'));
const SettingsPage = lazy(() => import('./SettingsPage'));
const StoreCustomizePage = lazy(() => import('./StoreCustomizePage'));
const StorePage = lazy(() => import('./StorePage'));
const AboutPage = lazy(() => import('./AboutPage'));
const AdminPage = lazy(() => import('./AdminPage'));
const AchievementsPage = lazy(() => import('./AchievementsPage'));
const AdoptionPage = lazy(() => import('./AdoptionPage'));
const DogWalkingPage = lazy(() => import('./DogWalkingPage'));
const AlertsPage = lazy(() => import('./AlertsPage'));
const AnalyticsPage = lazy(() => import('./AnalyticsPage'));
const TermsPage = lazy(() => import('./TermsPage'));
// PrivacyPage is handled by TermsPage based on pathname
const VerificationPage = lazy(() => import('./VerificationPage'));
const VerifyEmailPage = lazy(() => import('./VerifyEmailPage'));
const WantedPage = lazy(() => import('./WantedPage'));
const WishlistPage = lazy(() => import('./WishlistPage'));

// Route types kept for compatibility with existing route table format.
const G = 'grid', D = 'detail', F = 'form', L = 'list', C = 'chat', DB = 'dashboard', CT = 'content', P = 'profile';

// Route table: [pattern, Component, title, skeletonType]
const routes = [
  ['/', HomePage, 'PuiPi - 寵物二手用品交易平台', G],
  ['/sell', SellPage, '刊登商品', F],
  ['/search', SearchPage, '搜尋', G],
  ['/product/:id', ProductDetailPage, '商品詳情', D],
  ['/blog', BlogPage, '部落格', G],
  ['/blog/:slug', ArticlePage, '文章', CT],
  ['/auction/:id', AuctionDetailPage, '競標詳情', D],
  ['/auctions', AuctionPage, '競標', G],
  ['/bundle/:id', BundleDetailPage, '組合包詳情', D],
  ['/bundles', BundlesPage, '組合包', G],
  ['/categories', CategoriesPage, '分類', G],
  ['/charity', CharityPage, '公益', G],
  ['/chat', ChatPage, '聊天', C],
  ['/checkout/:productId', CheckoutPage, '結帳', F],
  ['/compare', ComparePage, '比較', G],
  ['/contact', ContactPage, '聯絡我們', F],
  ['/coupons', CouponsPage, '優惠券', L],
  ['/dashboard', DashboardPage, '儀表板', DB],
  ['/event/:id', EventDetailPage, '活動詳情', D],
  ['/events', EventsPage, '活動', G],
  ['/faq', FAQPage, '常見問題', CT],
  ['/favorites', FavoritesPage, '收藏', L],
  ['/feed', FeedPage, '動態', L],
  ['/forum', ForumPage, '討論區', G],
  ['/forum/:id', ForumPostPage, '討論文章', CT],
  ['/help', HelpCenterPage, '幫助中心', CT],
  ['/insurance', InsurancePage, '保險', CT],
  ['/lost-found', LostFoundPage, '協尋', G],
  ['/manage-products', ManageProductsPage, '管理商品', L],
  ['/map', MapPage, '地圖', G],
  ['/notifications', NotificationsPage, '通知', L],
  ['/orders', OrdersPage, '訂單', L],
  ['/pet-health', PetHealthPage, '寵物健康', CT],
  ['/profile', ProfilePage, '個人資料', P],
  ['/referral', ReferralPage, '推薦', CT],
  ['/returns', ReturnsPage, '退貨', L],
  ['/service/:id', ServiceDetailPage, '服務詳情', D],
  ['/services', ServicesPage, '服務', G],
  ['/settings', SettingsPage, '設定', F],
  ['/store/customize', StoreCustomizePage, '自訂商店', F],
  ['/store/:id', StorePage, '商店', P],
  ['/about', AboutPage, '關於我們', CT],
  ['/admin', AdminPage, '管理後台', DB],
  ['/achievements', AchievementsPage, '成就', G],
  ['/dog-walking', DogWalkingPage, '幫溜狗', G],
  ['/adoption', AdoptionPage, '認養', G],
  ['/alerts', AlertsPage, '提醒', L],
  ['/analytics', AnalyticsPage, '分析', DB],
  ['/terms', TermsPage, '服務條款', CT],
  ['/privacy', TermsPage, '隱私權政策', CT],
  ['/verification', VerificationPage, '驗證', F],
  ['/verify-email', VerifyEmailPage, '驗證信箱', F],
  ['/wanted', WantedPage, '徵物', F],
  ['/wishlists', WishlistPage, '願望清單', L],
  ['/wishlist/share/:code', WishlistPage, '分享願望清單', L],
  ['/wishlist/:id', WishlistPage, '願望清單', L],
  ['/user/:id', ProfilePage, '用戶資料', P],
];

function matchRoute(path) {
  for (const [pattern, Component, title, skel] of routes) {
    const params = matchPattern(pattern, path);
    if (params !== null) return { Component, params, title, skel };
  }
  return { Component: NotFoundPage, params: {}, title: '找不到頁面', skel: 'content' };
}

function matchPattern(pattern, path) {
  const patternParts = pattern.split('/');
  const pathParts = path.split('/');
  if (patternParts.length !== pathParts.length) return null;
  const params = {};
  for (let i = 0; i < patternParts.length; i++) {
    if (patternParts[i].startsWith(':')) {
      params[patternParts[i].slice(1)] = decodeURIComponent(pathParts[i]);
    } else if (patternParts[i] !== pathParts[i]) {
      return null;
    }
  }
  return params;
}

export default function AppRouter({ eager = false }) {
  const [mounted, setMounted] = useState(eager);
  useEffect(() => setMounted(true), []);

  // Don't render on server — avoids hydration mismatch since SPA routing is client-only
  if (!mounted) return null;

  return <AppRouterInner />;
}

function AppRouterInner() {
  const pathname = useSPAPathname();
  const { lang } = useSPARouter();

  // Strip lang prefix: /zh-tw/sell -> /sell
  const strippedPath = pathname.startsWith(`/${lang}`)
    ? pathname.slice(lang.length + 1) || '/'
    : pathname;

  const { Component, params, title } = useMemo(
    () => matchRoute(strippedPath),
    [strippedPath]
  );
  const pageMeta = useMemo(() => getPageMeta(strippedPath), [strippedPath]);

  // Set route params synchronously so children read correct values on first render
  setParams({ ...params, lang });

  // Update document title
  useEffect(() => {
    document.title = formatDocumentTitle(pageMeta.title || title);
  }, [pageMeta.title, title]);

  useEffect(() => {
    const description = pageMeta.description || DEFAULT_PAGE_META.description;
    let descriptionMeta = document.querySelector('meta[name="description"]');

    if (!descriptionMeta) {
      descriptionMeta = document.createElement('meta');
      descriptionMeta.setAttribute('name', 'description');
      document.head.appendChild(descriptionMeta);
    }

    descriptionMeta.setAttribute('content', description);
  }, [pageMeta.description]);

  return (
    <Suspense fallback={null}>
      <Component key={strippedPath} />
    </Suspense>
  );
}
